import { getAssetsUrlForCDN } from '~/utils/template';

// Sons da roletinha via Web Audio API — mesma estratégia iOS-safe do Giratudo.
// Regra de ouro do iPhone: o desbloqueio (unlock) tem que rodar SÍNCRONO dentro
// do gesto do usuário, ANTES de qualquer await. Por isso a decode fica separada.
//
// Fluxo: preload() (baixa os bytes) → unlock() (no gesto, síncrono) →
// ensureDecoded() (depois do gesto) → playTick()/playLand()/playWin().

export function useRouletteSounds() {
  // Resolvidas AQUI dentro (setup) — getAssetsUrlForCDN usa useRuntimeConfig(),
  // que não pode rodar no topo do módulo.
  const SOUND_TICK = getAssetsUrlForCDN('/assets/sounds/tick.wav');
  const SOUND_LAND = getAssetsUrlForCDN('/assets/sounds/land.wav');
  const SOUND_WIN = getAssetsUrlForCDN('/assets/sounds/celebration.wav');
  const ALL = [SOUND_TICK, SOUND_LAND, SOUND_WIN];

  let audioCtx: AudioContext | null = null;
  const rawBuffers = new Map<string, ArrayBuffer>();
  const audioBuffers = new Map<string, AudioBuffer>();
  let decoded = false;

  function getCtx(): AudioContext {
    if (!audioCtx) {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;

      audioCtx = new Ctx();
    }

    return audioCtx;
  }

  // Baixa os arquivos como ArrayBuffer (não precisa de AudioContext nem gesto).
  function preload() {
    ALL.forEach((url) => {
      if (rawBuffers.has(url)) return;

      fetch(url)
        .then((res) => res.arrayBuffer())
        .then((buf) => rawBuffers.set(url, buf))
        .catch(() => {});
    });
  }

  // DESBLOQUEIO iOS — chamar SÍNCRONO no handler do gesto, antes de qualquer
  // await. Toca 1 sample mudo + resume na mesma call stack: é o que o iOS exige.
  function unlock() {
    const ctx = getCtx();
    const silent = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();

    src.buffer = silent;
    src.connect(ctx.destination);
    src.start(0);

    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  }

  async function decode() {
    const ctx = getCtx();

    await Promise.all(
      ALL.map(async (url) => {
        if (audioBuffers.has(url)) return;

        let raw = rawBuffers.get(url);

        if (!raw) {
          try {
            raw = await (await fetch(url)).arrayBuffer();
            rawBuffers.set(url, raw);
          } catch {
            return;
          }
        }

        try {
          audioBuffers.set(url, await ctx.decodeAudioData(raw.slice(0)));
        } catch {
          /* ignore */
        }
      }),
    );

    decoded = true;
  }

  // Pode ser chamado após await (depois do unlock no gesto).
  async function ensureDecoded() {
    if (!decoded) await decode();
  }

  function play(url: string, volume = 1) {
    const ctx = getCtx();

    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const buffer = audioBuffers.get(url);

    if (!buffer) return;

    const src = ctx.createBufferSource();

    src.buffer = buffer;

    if (volume === 1) {
      src.connect(ctx.destination);
    } else {
      const gain = ctx.createGain();

      gain.gain.value = volume;
      src.connect(gain);
      gain.connect(ctx.destination);
    }

    src.start(0);
  }

  const playTick = () => play(SOUND_TICK, 0.6);
  const playLand = () => play(SOUND_LAND);
  const playWin = () => play(SOUND_WIN);

  // iOS suspende o contexto entre sessões — ao fechar, encerra e reseta.
  function dispose() {
    if (audioCtx && audioCtx.state !== 'closed') {
      audioCtx.close().catch(() => {});
    }

    audioCtx = null;
    audioBuffers.clear();
    decoded = false;
  }

  return {
    preload,
    unlock,
    ensureDecoded,
    playTick,
    playLand,
    playWin,
    dispose,
  };
}
